/**
 * WHERE THE MIGRATION FINISHED.
 *
 * Most of this shop's history was written by an import, not by the shop.
 * 1,774 jobs carry a `created_at` that is the moment the migration ran —
 * two minutes on 23 August for the bulk of it, and four more bulk minutes
 * spread across the 24th, 25th and 26th. Only 177 jobs were made by
 * somebody using the product.
 *
 * That makes `created_at` a lie on nine rows in ten, and every screen that
 * sorts, counts or windows by it inherits the lie. A "this month" figure
 * built on those dates is not wrong by a little.
 *
 * ─── WHAT THIS IS AND IS NOT ───────────────────────────────────────────
 *
 * It is a FLOOR under the day-to-day screens, not a delete. Every imported
 * row stays in the database, stays searchable, stays on its customer and
 * its vehicle, and stays in the job history where it belongs. What it does
 * not do is turn up in Waiting, on the railway, or in a count somebody is
 * about to believe.
 *
 * ─── AND IT IS ONE DATE, IN ONE PLACE ──────────────────────────────────
 *
 * The database has `import_watermark()` for the same reason. Writing the
 * date into a query is how the next correction misses half of them: this
 * one was three days short for a fortnight because four smaller import
 * batches were never noticed.
 */

/**
 * 2026-08-27. Two readings agree on it: the last bulk import minute is
 * 26 August 13:00, and it is when the shop says it started putting real
 * data in.
 */
export const IMPORT_WATERMARK = "2026-08-27T00:00:00.000Z";

/** Is this row the migration talking rather than the shop. */
export const isImported = (createdAt: string | null | undefined): boolean =>
  Boolean(createdAt) && String(createdAt) < IMPORT_WATERMARK;

/**
 * The floor for a screen, given the span somebody chose.
 *
 * `all` is the one that means it: somebody has deliberately asked to see
 * migrated history and has been told on the control that the dates will
 * look wrong. Everything else gets the later of its own window and the
 * watermark, so a "this month" that reaches back past the import cannot
 * quietly scoop it up.
 */
export function floorFor(spanFrom: string | null, includeImport: boolean): string | null {
  if (includeImport) return spanFrom;
  if (!spanFrom) return IMPORT_WATERMARK;
  return spanFrom > IMPORT_WATERMARK ? spanFrom : IMPORT_WATERMARK;
}
